import React, { useState } from 'react';
import { aiTutorAPI, track } from '../../services/api';
import MarkdownRenderer from './MarkdownRenderer';
import EthiopianFlag from '../EthiopianFlag';
import './TranslateAmharicButton.css';

/**
 * Per-content Amharic translate toggle.
 *
 * Tap → calls /ai-tutor/translate-am (Gemini, code-switched Amharic+English).
 * On success the translation is rendered below the original text — we keep
 * the English visible so students stay anchored to the exam's working
 * vocabulary. ~10s latency live; if a surface gets traction we'll
 * pre-translate the underlying static content offline and serve instant.
 *
 * Telemetry: every tap fires `ask_lomi.translate_amharic.clicked` with a
 * `surface` field (single funnel, easy to compare adoption per surface).
 */
interface TranslateAmharicButtonProps {
  /** Text to translate. Markdown + LaTeX both pass through Gemini intact. */
  text: string;
  /**
   * Where the button is mounted. Compared across surfaces in telemetry to
   * decide which static surfaces deserve offline pre-translation.
   * Examples: 'past_exam_explanation', 'key_definition', 'eslce_intel',
   *           'lesson_section', 'practice_mcq_explanation', 'assessment_explanation'
   */
  surface: string;
  /** Optional extra fields forwarded to the telemetry event. */
  trackProps?: Record<string, unknown>;
  /**
   * Stable identity for the translated content (e.g. component_id,
   * question_id, term name). Two buttons with the same key share the
   * cached translation, so re-mounting doesn't re-fetch.
   */
  cacheKey: string;
  /** Visible label next to the flag. Defaults to "አማርኛ". */
  label?: string;
  /** Compact variant (smaller padding) for tight surfaces like definition rows. */
  compact?: boolean;
  /**
   * Optional content-type hint forwarded to the API. Selects a tailored prompt
   * server-side (e.g. 'key_term' keeps a definition terse) and namespaces the
   * server cache. Omit for the default free-form prompt.
   */
  contentType?: string;
}

// In-memory cache shared across mounts. Keyed by surface + cacheKey so
// past_exam_explanation:Q123 doesn't collide with eslce_intel:cA456.
const _amCache = new Map<string, string>();

const TranslateAmharicButton: React.FC<TranslateAmharicButtonProps> = ({
  text,
  surface,
  trackProps,
  cacheKey,
  label = 'አማርኛ',
  compact = false,
  contentType,
}) => {
  const fullKey = `${surface}:${cacheKey}`;
  const [translated, setTranslated] = useState<string | null>(_amCache.get(fullKey) || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!text || !text.trim()) return null;

  const onClick = async () => {
    if (loading || translated) return;
    track('ask_lomi.translate_amharic.clicked', {
      surface,
      char_count: text.length,
      ...(trackProps || {}),
    });
    setError(null);
    setLoading(true);
    try {
      const result = await aiTutorAPI.translateAmharic(text, contentType);
      if (result?.amharic_text) {
        _amCache.set(fullKey, result.amharic_text);
        setTranslated(result.amharic_text);
      } else {
        setError('Translation came back empty.');
      }
    } catch (err) {
      console.error(`[translate-am ${surface}] failed:`, err);
      setError('Could not translate. Try again.');
    } finally {
      setLoading(false);
    }
  };

  if (translated) {
    return (
      <div className={`am-translate-result ${compact ? 'compact' : ''}`}>
        <MarkdownRenderer>{translated}</MarkdownRenderer>
      </div>
    );
  }

  // Loading state: replace the button with a "thinking" bubble that
  // matches the warm-yellow result panel — same colors, same shape, just
  // with animated dots. Visually morphs into the translation when it
  // arrives. Mirrors the LLM typing-bubble pattern people already
  // recognize from chat AIs, signalling "this is AI, give it a moment"
  // instead of leaving them staring at a static "Translating…" label.
  if (loading) {
    return (
      <div className={`am-translate-loading ${compact ? 'compact' : ''}`} role="status" aria-live="polite">
        <EthiopianFlag size={compact ? 14 : 16} style={{ marginRight: 8, flexShrink: 0 }} />
        <span className="am-translate-loading-dots" aria-hidden="true">
          <span></span><span></span><span></span>
        </span>
        <span className="am-translate-loading-text">Translating to Amharic…</span>
      </div>
    );
  }

  return (
    <div className={`am-translate-wrapper ${compact ? 'compact' : ''}`}>
      <button
        type="button"
        className={`am-translate-btn ${compact ? 'compact' : ''}`}
        onClick={onClick}
        title="Translate to Amharic (mixed with English exam vocabulary)"
      >
        <EthiopianFlag size={compact ? 12 : 14} style={{ marginRight: 6 }} />
        {label}
      </button>
      {error && <span className="am-translate-error">⚠️ {error}</span>}
    </div>
  );
};

export default TranslateAmharicButton;
